import React, { useState, useEffect } from "react";
import { Ripple } from "primereact/ripple";
import { Dialog } from 'primereact/dialog';
import { Button } from "primereact/button";
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { InputNumberChangeEvent } from 'primereact/inputnumber'
import TextInput from "../../../../../Components/TextInput";
import FileInput from "../../../../../Components/FileInput";
import NumberInput from "../../../../../Components/NumberInput";
import { checkFileOthers, checkNumber, checkText } from "../../../../../Utils/Validator";
import { paymentCertificateCreateAPI, paymentCertificateUpdateAPI } from "../../../../../Api/Project/DefaultServices/PaymentCertificateApi";
import { PaymentCertificates } from "../../../../../Controllers/Types/Project/DefaultService/PaymentCertificateInterface";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { getAppCurrencyType } from "../../../../../Utils/Common";

interface PaymentCertificateModalProps {
    isOpen: boolean;
    dataState: 'Add' | 'Edit';
    onClose: () => void;
    onFetchData: () => void;
    toast: React.RefObject<Toast>;
    serviceId: number;
    selectedPayCerData?: PaymentCertificates
}

const PaymentCertificateModal: React.FC<PaymentCertificateModalProps> = ({
    isOpen,
    dataState,
    onClose,
    onFetchData,
    toast,
    serviceId,
    selectedPayCerData
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [statuses] = useState<string[]>(['Paid', 'Unpaid']);
    const vatPercentage = 5;
    const currencySymbol = getAppCurrencyType();

    /********************************* Form variables *************************************/
    const [certificateNumber, setCertificateNumber] = useState<string>('');
    const [status, setStatus] = useState<string | null>(null);
    const [totalAmount, setTotalAmount] = useState<number | null>(null);
    const [retentionAmount, setRetentionAmount] = useState<number | null>(null);
    const [vat, setVat] = useState<number | null>(null);
    const [certificateAmount, setCertificateAmount] = useState<number | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>('');

    /* Error message */
    const [certificateNumberError, setCertificateNumberError] = useState<string>('');
    const [statusError, setStatusError] = useState<string>('');
    const [totalAmountError, setTotalAmountError] = useState<string>('');
    const [retentionAmountError, setRetentionAmountError] = useState<string>('');
    const [vatError, setVatError] = useState<string>('');
    const [certificateAmountError, setCertificateAmountError] = useState<string>('');
    const [fileError, setFileError] = useState<string>('');
    /* ********************************************************************************** */

    const [updateHasChanges, setUpdateHasChanges] = useState<boolean>(false);

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const isPermission_change_progress = useCanEdit('Payment Certificate', 'progress_options');


    useEffect(() => {
        setDataValue();
    }, [dataState, selectedPayCerData]);

    function setDataValue() {
        if (dataState === "Add") {
            handleClearFields();
        } else { // Edit
            setCertificateNumber(selectedPayCerData?.certificate_no!);
            setStatus(selectedPayCerData?.status!);
            setTotalAmount(selectedPayCerData?.total_amount!);
            setRetentionAmount(selectedPayCerData?.retention_amount!);
            setVat(selectedPayCerData?.vat!);
            setCertificateAmount(selectedPayCerData?.certificate_amount!);
            setFile(null);
            setFileName('');
        }
    }

    const handleClearFields = () => {
        setCertificateNumber('');
        setStatus(null);
        setTotalAmount(null);
        setRetentionAmount(null);
        setVat(null);
        setCertificateAmount(null);
        setFile(null);
        setFileName('');
    }

    const handleClearErrors = () => {
        setCertificateNumberError('');
        setStatusError('');
        setTotalAmountError('');
        setRetentionAmountError('');
        setVatError('');
        setCertificateAmountError('');
        setFileError('');
    }

    /* Clear errors */
    useEffect(() => {
        checkText(certificateNumber, false, setCertificateNumberError);
    }, [certificateNumber]);

    useEffect(() => {
        checkNumber(totalAmount!, false, setTotalAmountError);
    }, [totalAmount]);

    useEffect(() => {
        checkNumber(retentionAmount!, false, setRetentionAmountError);
    }, [retentionAmount]);

    useEffect(() => {
        checkNumber(vat!, false, setVatError);
    }, [vat]);

    useEffect(() => {
        checkNumber(certificateAmount!, false, setCertificateAmountError);
    }, [certificateAmount]);

    useEffect(() => {
        checkText(status!, false, setStatusError);
    }, [status]);

    useEffect(() => {
        checkFileOthers(file!, false, setFileError);
    }, [file]);
    /*  */


    function checkCredentials(): boolean {
        const isCertificateNumberValid = checkText(certificateNumber, true, setCertificateNumberError);
        const isTotalAmountValid = checkNumber(totalAmount!, true, setTotalAmountError);
        const isRetentionAmountValid = checkNumber(retentionAmount!, true, setRetentionAmountError);
        const isVatValid = checkNumber(vat!, true, setVatError);
        const isCertificateAmountValid = checkNumber(certificateAmount!, true, setCertificateAmountError);
        const isStatusValid = checkText(status!, true, setStatusError);
        const isFileValid = checkFileOthers(file!, false, setFileError);

        return isCertificateNumberValid && isTotalAmountValid && isRetentionAmountValid && isVatValid && isCertificateAmountValid && isStatusValid && isFileValid;
    }


    /* *************************************************  create ***************************************** */
    const handleCreateDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setLoading(true);
            const data = await paymentCertificateCreateAPI(
                Number(serviceId),
                certificateNumber,
                totalAmount || 0,
                retentionAmount || 0,
                vat || 0,
                certificateAmount || 0,
                status!,
                file!
            );

            setLoading(false);

            if (data.statusCode === 200) {
                onFetchData();
                handleCloseModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Certificate details added successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating certificate.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* *************************************************  create ***************************************** */

    /* *************************************************  Update ***************************************** */
    useEffect(() => {
        if (selectedPayCerData) {
            if (certificateNumber !== selectedPayCerData?.certificate_no) {
                setUpdateHasChanges(true);
            } else if (status! !== selectedPayCerData?.status) {
                setUpdateHasChanges(true);
            } else if (totalAmount! !== selectedPayCerData?.total_amount) {
                setUpdateHasChanges(true);
            } else if (retentionAmount! !== selectedPayCerData?.retention_amount) {
                setUpdateHasChanges(true);
            } else if (vat! !== selectedPayCerData?.vat) {
                setUpdateHasChanges(true);
            } else if (certificateAmount! !== selectedPayCerData?.certificate_amount) {
                setUpdateHasChanges(true);
            } else if (file !== null) {
                setUpdateHasChanges(true);
            } else {
                setUpdateHasChanges(false);
            }
        }
    }, [selectedPayCerData, certificateNumber, status, totalAmount, retentionAmount, vat, certificateAmount, file])

    const handleUpdateDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setLoading(true);

            const data = await paymentCertificateUpdateAPI(
                Number(selectedPayCerData?.id),
                certificateNumber, totalAmount || 0,
                retentionAmount || 0,
                vat || 0,
                certificateAmount || 0,
                status!,
                file!
            );

            setLoading(false);

            if (data.statusCode === 200) {
                onFetchData();
                handleCloseModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Certificate details updated successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed.',
                        detail: 'There was an error updating certificate.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* *************************************************  Update ***************************************** */

    const onStatusChange = (e: any) => {
        setStatus(e.value);
    };

    const handleTotalAmountChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setTotalAmount(e.value);
        } else {
            setTotalAmount(null);
        }
    };

    const handleRetentionAmountChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setRetentionAmount(e.value);
        } else {
            setRetentionAmount(null)
        }
    };

    const handleVatChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setVat(e.value);
        } else {
            setVat(null);
        }
    };

    const handleCertificateAmountChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setCertificateAmount(e.value);
        } else {
            setCertificateAmount(null);
        }
    };

    const handleCloseModal = () => {
        onClose();
        handleClearFields();
        handleClearErrors();
    }


    /******************************* For drawing file upload ********************************/
    const handleFileUpload = (file: File) => {
        setFile(file);
        setFileName(file.name);
    };

    const handleClearFile = () => {
        setFile(null);
        setFileName('');
    };
    /****************************************************************************************/


    /* PaymentCertificateModal Modal header */
    const detailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;certificate detail
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const detailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateDetails : handleUpdateDetails}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !updateHasChanges}
            />
        </div>
    );
    /*  */

    return (
        <Dialog
            visible={isOpen}
            header={detailModalHeader}
            footer={detailModalFooter}
            headerClassName="custom_modal_header"
            className="custom_modal_dialog modal_dialog_md"
            onHide={handleCloseModal}
            dismissableMask
        >
            <div className="custom_modal_body">
                <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                    <div className="row">
                        {/*Certificate no */}
                        <div className="col-12 col-sm-6">
                            <TextInput
                                id="certificateNumber"
                                label="Certificate no."
                                labelHtmlFor="certificateNumber"
                                required={true}
                                value={certificateNumber}
                                placeholder="Certificate no."
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCertificateNumber(e.target.value)}
                                error={certificateNumberError}
                            />
                        </div>

                        {/* Status */}
                        <div className="col-12 col-sm-6">
                            <div className="page_form_group">
                                <label htmlFor="status" className="custom_form_label is_required">Status</label>
                                <Dropdown
                                    id="status"
                                    value={status}
                                    options={statuses}
                                    onChange={onStatusChange}
                                    placeholder="Select status"
                                    className="form_dropdown w-100"
                                    showClear
                                    disabled={!isPermission_change_progress}
                                />

                                {statusError &&
                                    <small className="form_error_msg">{statusError}</small>
                                }
                            </div>
                        </div>

                        {/* Total amount */}
                        <div className="col-12 col-sm-6">
                            <NumberInput
                                inputId="totalAmount"
                                label="Total amount"
                                labelHtmlFor="totalAmount"
                                placeholder="Total amount"
                                onChange={handleTotalAmountChange}
                                value={totalAmount}
                                required={true}
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                min={0}
                                error={totalAmountError}
                                prefix={currencySymbol + ' '}
                            />
                        </div>

                        {/* Retention amount */}
                        <div className="col-12 col-sm-6">
                            <NumberInput
                                inputId="retentionAmount"
                                label="Retention amount"
                                labelHtmlFor="retentionAmount"
                                placeholder="Retention amount"
                                onChange={handleRetentionAmountChange}
                                value={retentionAmount}
                                required={true}
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                min={0}
                                error={retentionAmountError}
                                prefix={currencySymbol + ' '}
                            />
                        </div>

                        {/* VAT */}
                        <div className="col-12 col-sm-6">
                            <NumberInput
                                inputId="vat"
                                label={`VAT (${vatPercentage}%)`}
                                labelHtmlFor="vat"
                                placeholder="VAT"
                                onChange={handleVatChange}
                                value={vat}
                                required={false}
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                min={0}
                                error={vatError}
                                prefix={currencySymbol + ' '}
                            />
                        </div>

                        {/* Certificate amount */}
                        <div className="col-12 col-sm-6">
                            <NumberInput
                                inputId="certificateAmount"
                                label="Certificate amount"
                                labelHtmlFor="certificateAmount"
                                placeholder="Certificate amount"
                                onChange={handleCertificateAmountChange}
                                value={certificateAmount}
                                required={true}
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                min={0}
                                error={certificateAmountError}
                                prefix={currencySymbol + ' '}
                            />
                        </div>

                        {/* File */}
                        <div className="col-12">
                            <FileInput
                                id="file"
                                label="Upload file"
                                labelHtmlFor="file"
                                // required={dataState === 'Add' ? true : false}
                                required={false}
                                value={fileName}
                                onUpload={handleFileUpload}
                                onClear={handleClearFile}
                                toast={toast}
                                error={fileError}
                                containerClassName="mb-0"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Dialog >
    )
}

export default PaymentCertificateModal;